// @flow
import { createAction, disbatch } from 'redux-act';

import { selectIsAuthenticatedUser } from '@gandi/authentication';
import { selectPricingInfos, type Currency } from '@gandi/billing';
import {
  getCart,
  addCartProduct,
  removeProducts,
  addCartProducts,
  selectProductsFromFqdn,
  type ProductData,
  type ProductPayload,
  type ProductType,
  type Process,
} from '@gandi/cart';
import type { Fqdn } from '@gandi/domain-configuration';
import type { HTTPStatusCodes } from '@gandi/fetcher';
import type { FetchResponse } from '@gandi/resource/redux';
import type { TranslatorProps } from '@gandi/translate';

import { GET } from 'gandi.redux-fetcher';
import { createLogger } from 'gandi.redux-logger';

import {
  reducePrices,
  fetchQuotation,
} from 'src/DomainRenew/components/modules/actions';
import type {
  QuotedProduct,
  PriceApiResult,
} from 'src/DomainRenew/components/types';
import type { RenewInfo } from 'src/MailboxCreate/types';

import type { Dispatch, GetState, ThunkAction } from 'src/types';

import type {
  ExtendedResult,
  QuotationProduct,
  Result,
  BulkProducts,
} from '../types';

import { appendServicesBulkData } from './domainServices';
import { fetchAvailability, fetchTransferable } from './fallbacks';

import {
  selectSearchValue,
  selectResults,
  selectRenewableResultsToAdd,
} from './selectors';

const logger = createLogger('renew/restore');

export const setSearchValue: {
  (...Array<string>): { meta?: mixed, payload: mixed, type: string, ... },
  assignTo: mixed => void,
  assigned: () => boolean,
  bindTo: mixed => mixed,
  bound: () => boolean,
  dispatched: () => boolean,
  getType: () => string,
  raw: () => mixed,
  toString: () => string,
  ...
} = createAction('[domain/renew] Set search value');
export const setLoading: {
  (...Array<boolean>): { meta?: mixed, payload: mixed, type: string, ... },
  assignTo: mixed => void,
  assigned: () => boolean,
  bindTo: mixed => mixed,
  bound: () => boolean,
  dispatched: () => boolean,
  getType: () => string,
  raw: () => mixed,
  toString: () => string,
  ...
} = createAction('[domain/renew] Set search loading state');

export const setDomainLoading: {
  (...Array<{ fqdn: string, loading: boolean }>): {
    meta?: mixed,
    payload: mixed,
    type: string,
    ...
  },
  assignTo: mixed => void,
  assigned: () => boolean,
  bindTo: mixed => mixed,
  bound: () => boolean,
  dispatched: () => boolean,
  getType: () => string,
  raw: () => mixed,
  toString: () => string,
  ...
} = createAction('[domain/renew] Set domain loading state');
export const setDomainInfo: {
  (
    ...| Array<{
        fqdn: string,
        info: {
          availability: string,
          extraneous: boolean,
          transferable: boolean,
        },
      }>
    | Array<{ fqdn: string, info: { invalid: boolean } }>
    | Array<{ fqdn: Fqdn }>
    | Array<{ fqdn: Fqdn, info: any }>
  ): { meta?: mixed, payload: mixed, type: string, ... },
  assignTo: mixed => void,
  assigned: () => boolean,
  bindTo: mixed => mixed,
  bound: () => boolean,
  dispatched: () => boolean,
  getType: () => string,
  raw: () => mixed,
  toString: () => string,
  ...
} = createAction('[domain/renew] Set domain restore/renew info');
export const setDomainPrice: {
  (...Array<QuotedProduct | number | Array<QuotedProduct>>): {
    meta?: mixed,
    payload: mixed,
    type: string,
    ...
  },
  assignTo: mixed => void,
  assigned: () => boolean,
  bindTo: mixed => mixed,
  bound: () => boolean,
  dispatched: () => boolean,
  getType: () => string,
  raw: () => mixed,
  toString: () => string,
  ...
} = createAction('[domain/renew] Set domain restore/renew price');
export const setCartProductState: {
  (...Array<{ cartProductState: string, fqdn: string }>): {
    meta?: mixed,
    payload: mixed,
    type: string,
    ...
  },
  assignTo: mixed => void,
  assigned: () => boolean,
  bindTo: mixed => mixed,
  bound: () => boolean,
  dispatched: () => boolean,
  getType: () => string,
  raw: () => mixed,
  toString: () => string,
  ...
} = createAction('[domain/renew] Set domain cart product state');
export const resetResults: {
  (...Array<any>): { meta?: mixed, payload: mixed, type: string, ... },
  assignTo: mixed => void,
  assigned: () => boolean,
  bindTo: mixed => mixed,
  bound: () => boolean,
  dispatched: () => boolean,
  getType: () => string,
  raw: () => mixed,
  toString: () => string,
  ...
} = createAction('[domain/renew] Reset results');

const getProcess = (domain: ExtendedResult) => {
  if (domain.renewable) {
    return 'renew';
  }

  if (domain.restorable) {
    return 'restore';
  }

  return '';
};

function getResultProductData(domain: ExtendedResult): {
  extra: { identifier?: string, label?: string, optimisticId?: string },
  productData: {
    authentication?: { password: string },
    bundle_id?: number,
    fqdn?: string,
    identifier?: string,
    label?: string,
    metadatas?: {
      corporate: boolean,
      fqdn: string,
      lang?: any,
      position: number,
      query: string,
      real_position?: any,
      selected_filter: Array<string>,
      ...
    },
    payload?: ProductPayload,
    process: Process,
    product_type: ProductType,
    service_id?: string,
  },
} {
  const process = getProcess(domain);

  const productData = {
    product_type: 'domain',
    process,
    identifier: domain.fqdn,
    payload: {
      duration: domain.minimalDuration,
    },
  };

  const label = domain.fqdn;

  const extra = { label };

  return {
    // $FlowFixMe[incompatible-return]
    productData,
    extra,
  };
}

const fetchRenewInfo =
  (fqdn: Fqdn): ((Dispatch, GetState) => any) =>
  (dispatch, getState) => {
    const isAuthenticated = selectIsAuthenticatedUser(getState());
    const endpoint = isAuthenticated ? 'domain' : 'domain-anonymous';
    return dispatch(
      GET(`/${endpoint}/domains/${fqdn}/renew`, {
        // XXX: improve response payload please
        on200: (
          response: FetchResponse<200, { renew: RenewInfo } | RenewInfo>,
        ) => {
          // T54746
          const data = response.data.renew || response.data;
          dispatch(setDomainInfo({ info: data, fqdn }));
        },
        not200: (response: FetchResponse<HTTPStatusCodes, any>) => {
          // T54746
          if ('renew' in response.data) {
            response.data = response.data.renew;
          }
          throw response;
        },
      }),
    ).catch(err => {
      dispatch(setDomainInfo({ fqdn }));
      if (err.status !== 404) {
        dispatch(logger.error({ err }, 'Unable to fetch renew info'));
      }
      throw err;
    });
  };

export const removeFromCart =
  (domain: ExtendedResult): ThunkAction<Promise<void>> =>
  async (dispatch, getState) => {
    const { fqdn } = domain;

    const productsToRemove = selectProductsFromFqdn(getState(), fqdn);

    if (productsToRemove.length) {
      dispatch(setCartProductState({ fqdn, cartProductState: 'removing' }));
      try {
        // $FlowFixMe refine @gandi/cart Product
        await dispatch(removeProducts(productsToRemove));
      } catch (err) {
        dispatch(
          logger.error(
            { err },
            'Unable to remove domain renew/restore product from cart.',
          ),
        );
      }
      dispatch(setCartProductState({ fqdn, cartProductState: 'none' }));
    }
  };

const fetchRestoreInfo =
  (fqdn: Fqdn): (Dispatch => Promise<void>) =>
  async dispatch => {
    const promise = dispatch(
      GET(`/domain-anonymous/domains/${fqdn}/restore`, {
        on200: (response: FetchResponse<>) =>
          dispatch(setDomainInfo({ info: response.data, fqdn })),
        not200: (response: FetchResponse<>) => Promise.reject(response),
      }),
    );

    try {
      await promise;
    } catch (err) {
      dispatch(setDomainInfo({ fqdn }));
      if (err.status !== 404) {
        dispatch(logger.error({ err }, 'Unable to fetch restore info'));
      }
    }
  };

const loadResults = (): ThunkAction<Promise<mixed>> => (dispatch, getState) => {
  const searchValue = selectSearchValue(getState());
  const splittedSearchValue = searchValue.split(' ');

  const promises = splittedSearchValue.map(fqdn => {
    dispatch(setDomainLoading({ fqdn, loading: true }));
    return dispatch(fetchRenewInfo(fqdn))
      .catch(renewErr => {
        if (renewErr.status === 400) {
          /* invalid domain, query aiosuggest for some domain suggestions */
          dispatch(setDomainInfo({ info: { invalid: true }, fqdn }));
          return Promise.resolve();
        }

        if (renewErr.status === 404) {
          let availability = 'unknown';
          let transferable = false;
          /* valid domain but not at gandi, suggest create / whois + transfer */
          return dispatch(fetchAvailability(fqdn))
            .then(availabilityResponse => {
              availability = availabilityResponse.data.availability;

              // domain might be transferable.
              if (availability !== 'available') {
                return dispatch(fetchTransferable(fqdn)).then(
                  transferableResponse => {
                    transferable = !!transferableResponse.data.available;
                  },
                );
              }

              return Promise.resolve();
            })
            .catch(() => {
              // pass
            })
            .then(() => {
              dispatch(
                setDomainInfo({
                  info: { extraneous: true, availability, transferable },
                  fqdn,
                }),
              );
              throw renewErr;
            });
        }

        throw renewErr;
      })
      .then(() => {
        // FIXME: add a single result selector
        const results = selectResults(getState());
        const result = results[fqdn];
        if (result && !result.renewable && !result.invalid) {
          return dispatch(fetchRestoreInfo(fqdn));
        }
        return Promise.resolve();
      })
      .catch(() => {
        /* pass */
      })
      .then(() => dispatch(setDomainLoading({ fqdn, loading: false })));
  });

  return Promise.allSettled(promises);
};

export const validateSearch =
  (inputValue: string): (Dispatch => Promise<void>) =>
  async dispatch => {
    disbatch(
      dispatch,
      setSearchValue(inputValue),
      resetResults(),
      inputValue ? setLoading(true) : setLoading(false),
    );

    if (!inputValue) return;

    try {
      await dispatch(loadResults());
    } finally {
      dispatch(setLoading(false));
    }
  };

export type FetchResultsQuotation<R> = () => R;
export type FetchResultsQuotationReturn = Promise<void>;
export const fetchResultsQuotation: FetchResultsQuotation<
  ThunkAction<FetchResultsQuotationReturn>,
> = () => async (dispatch, getState) => {
  const results = selectResults(getState());
  const products: Array<QuotationProduct> = Object.keys(results)
    .map(fqdn => results[fqdn])
    .filter(result => result.renewable || result.restorable)
    .map(result => ({
      // $FlowFixMe[prop-missing]
      process: getProcess(result),
      type: 'domain',
      identifier: result.fqdn,
      payload: {
        duration: result.minimalDuration || 1,
      },
    }));

  if (!products.length) {
    return;
  }

  const pricingInfos = selectPricingInfos(getState());

  const body = {
    ...pricingInfos,
    products,
  };

  const quotationsData: {
    currency: Currency,
    prices: Array<PriceApiResult>,
    ...
  } =
    // $FlowFixMe[prop-missing]
    // $FlowFixMe[incompatible-call]
    await dispatch(fetchQuotation(body));

  const quotedProducts = products.map(product =>
    reducePrices(
      quotationsData.prices,
      product.identifier,
      quotationsData.currency,
      product.type,
    ),
  );

  if (quotedProducts.length) {
    disbatch(dispatch, quotedProducts.map(setDomainPrice));
  }
};

// `addRenewableAndServices` bulk
// the renew result if not in the cart
// with their services if user is authenticated
export const addRenewableAndServices =
  (
    productToAdd: Array<Result>,
    translator: TranslatorProps,
  ): (Dispatch => Promise<void> | Promise<mixed>) =>
  async dispatch => {
    if (!productToAdd.length) {
      return Promise.resolve();
    }

    let bulkProducts: BulkProducts = productToAdd.reduce(
      (
        acc: {
          data: Array<ProductData>,
          extra: {
            [key: string]: {
              label: string,
              identifier: string,
            },
          },
        },
        current: Result,
      ) => {
        // feed the bulk with domains
        const productData = {
          product_type: 'domain',
          process: current.renewable ? 'renew' : 'restore',
          identifier: current.fqdn,
          payload: {
            duration: current.minimalDuration,
          },
        };

        acc.data.push(productData);
        acc.extra[current.fqdn] = {
          label: current.fqdn,
          identifier: current.fqdn,
        };

        return acc;
      },
      { data: [], extra: {} },
    );

    bulkProducts = await dispatch(
      appendServicesBulkData(translator, bulkProducts),
    );

    const baseAddCartPayload = {
      extra: bulkProducts.extra,
      options: {
        headers: { 'X-Dry-Run': 0 },
      },
    };

    // in case of error, it is useful to have the minimalist call
    if (bulkProducts.data.length === 1) {
      await dispatch(
        addCartProduct({
          ...baseAddCartPayload,
          productData: bulkProducts.data[0],
        }),
      );
    } else {
      await dispatch(
        addCartProducts({
          ...baseAddCartPayload,
          productsData: bulkProducts.data,
        }),
      );
    }
    return dispatch(getCart());
  };

export const addRewableResults =
  (translator: TranslatorProps): ThunkAction<Promise<any>> =>
  (dispatch, getState) =>
    dispatch(
      addRenewableAndServices(
        selectRenewableResultsToAdd(getState()),
        translator,
      ),
    );

export const addToCart =
  (
    domain: ExtendedResult,
    translator: TranslatorProps,
  ): ThunkAction<Promise<void>> =>
  async (dispatch, getState) => {
    const { fqdn } = domain;

    const { productData, extra } = getResultProductData(domain);
    const { process } = productData;

    const authenticated = selectIsAuthenticatedUser(getState());

    // Add the product via the API, bypass dry-run.
    const promise = authenticated
      ? dispatch(addRenewableAndServices([domain], translator))
      : dispatch(
          addCartProduct({
            // $FlowFixMe use Process instead of a string
            productData,
            extra,
            options: {
              headers: { 'X-Dry-Run': 0 },
            },
          }),
        );

    dispatch(setCartProductState({ fqdn, cartProductState: 'adding' }));

    try {
      try {
        await promise;
      } catch (err) {
        dispatch(
          logger.error(
            { err },
            `Unable to add domain_${process} product to cart.`,
          ),
        );
        throw err;
      }
      await dispatch(getCart());
    } finally {
      dispatch(setCartProductState({ fqdn, cartProductState: 'none' }));
    }
  };
